import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import Link from 'next/link';

import styles from './MillesimaLink.module.scss';

// Use either innerHtml or children props to set content in link but not both
// customClassName is an object with "container" and "link" property
const MillesimaLink = ({
  type = 'link', id, href, as = '', ariaLabel, size = '', onClick, customClassName,
  innerHtml, children, isNofollow, isWithoutContainer, isTargetBlank, isNextLink, forceReload,
  onMouseEnter, onMouseLeave, style
}) => {
  const router = useRouter();

  const rbfId = id ? id + '-' : '';

  const className = [
    styles.container,
    customClassName?.container
  ].filter(Boolean).join(' ');

  const contentProps = {};

  if (children) {
    contentProps.children = children;
  } else {
    contentProps.dangerouslySetInnerHTML = { __html: innerHtml };
  }

  const link = () => {
    const linkProps = {
      'aria-label': ariaLabel,
      onClick,
      ...contentProps,
      className: [
        styles[type],
        styles[size],
        customClassName?.link
      ].filter(Boolean).join(' '),
      onMouseEnter,
      onMouseLeave,
      style,
      target: isTargetBlank ? '_blank' : null
    };

    return (href?.query?.type || isNextLink) ? (
      <Link
        href={href}
        as={as}
        locale={router.locale}
        {...linkProps}
        data-rbf={`${rbfId}followed`}
        data-test-id="link-followed"
      />
    ) : (
      <a {...linkProps} href={as || href} data-rbf={`${rbfId}followed`} data-test-id="a-followed" />
    );
  };

  const handleClick = () => {
    onClick?.();

    if (isTargetBlank) {
      Object.assign(document.createElement('a'), {
        href,
        target: '_blank'
      }).click();
    } else if (forceReload) {
      window.location = href;
    } else {
      router.push(href, as);
    }
  };

  return (
    <>
      {isNofollow ? (
        <span {...contentProps} className={className} onClick={handleClick} data-rbf={`${rbfId}unfollowed`} data-test-id="link-unfollowed" />
      ) : (
        <>
          {isWithoutContainer ? (
            link()
          ) : (
            <div className={className} data-test-id="link-container">
              {link()}
            </div>
          )}
        </>
      )}
    </>
  );
};

MillesimaLink.propTypes = {
  type: PropTypes.oneOf(['inline-link', 'link', 'no-decoration']),
  href: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
  ]).isRequired,
  as: PropTypes.string,
  innerHtml: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  onClick: PropTypes.func,
  customClassName: PropTypes.shape({
    container: PropTypes.string,
    link: PropTypes.string
  }),
  children: PropTypes.node,
  isNofollow: PropTypes.bool,
  isWithoutContainer: PropTypes.bool,
  isTargetBlank: PropTypes.bool,
  isNextLink: PropTypes.bool,
  forceReload: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  style: PropTypes.object
};

export default MillesimaLink;
